import { JSX, useEffect, useState } from 'react';
import { connect, ConnectedProps, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSetPageTitle } from '../../hooks/useSetPageTitle';
import Layout from '../../components/layouts/layout-login';
import BackgroundImage from '../../assets/video/invisibly-brand-img.mp4';
import { useParams } from "react-router-dom";
import { Lock } from 'lucide-react';
import { signInWithUuid } from '../../redux/login/login.async-thunks';
import { ReduxState } from '../../redux/redux-state';
import { getIsUserAuthenticated } from '../../redux/user/user.selectors';
import { isClientUser, isOrgUser, isB2BUser } from 'src/services/permission/permission.helpers';

type Params = { uuid?: string };

interface Props extends PropsFromRedux {
}

function EmailSignInPage({ isAuthenticated, emailSignInWithUuid }: Props): JSX.Element {
  useSetPageTitle('Email Sign In | Invisibly');
  const navigate = useNavigate();
  const [isFailed, setIsFailed] = useState(false);
  const { uuid } = useParams<Params>();
  const userState = useSelector((state: ReduxState) => state.userState);

  useEffect(() => {
    if (isAuthenticated) {
      if (isClientUser(userState.permissions)) {
        navigate('/client/reports-old');
      } else if (isOrgUser(userState.permissions)) {
        navigate('/dashboard');
      } else if (isB2BUser(userState.permissions)) {
        navigate('/');
      }
    }
  }, [isAuthenticated, userState.permissions?.groups, navigate]);

  useEffect(() => {
    if (uuid) {
      const handleEmailSignInWithUuid = async() => {
        const response = await emailSignInWithUuid(uuid);
        if (response?.error?.message.includes('Rejected')) {
          setIsFailed(true);
        }
      };
      handleEmailSignInWithUuid();
    }

  }, [uuid, emailSignInWithUuid]);

  return (
    <Layout
      imgComponent={(
        <div className='absolute inset-0 overflow-hidden'>
          <video
            className='size-full object-cover'
            autoPlay
            loop
            muted
          >
            <source
              src={BackgroundImage}
              type='video/mp4'
            />
          </video>
        </div>
      )}
      formComponent={(
        <div className='flex h-screen justify-center bg-gray-200'>
          <div className='flex max-w-[25rem] flex-col items-center justify-center'>
            <div className='shadow-md p-4 rounded-lg bg-white'>
              <h3 className='flex items-center gap-2 text-xl animate-pulse'>
                <Lock className='w-6 h-6' />
                {isFailed ? 'Failed to redirect, please try again' : 'Redirecting ...'}
              </h3>
              <div className='text-center'>
                <p className='text-sm mt-2'>
                  {isFailed ? 'There was an error redirecting you to the login page, please try again or contact support' : 'Please wait while we redirect you to the login page...'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    />
  );
}

const mapDispatchToProps = (dispatch: any) => ({
  emailSignInWithUuid: (uuid: string) => dispatch(signInWithUuid(uuid)),
});

const mapStateToProps = (state: ReduxState) => ({
  isAuthenticated: getIsUserAuthenticated(state),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(EmailSignInPage);
